import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { Formik, Field, FormikErrors, FieldProps } from 'formik'
import { IntlShape } from 'react-intl'
import * as Yup from 'yup'
import {
  roundMoney,
  tierPricePerPersonFromDiscount,
  tierTotalTTCFromDiscount,
  discountFromTierTotal,
} from './discountTierSync'

export interface TourDiscount {
  _id?: string
  tour: string
  minParticipants: number
  discountType: 'fixed_amount' | 'percentage'
  discountValue: number
  startDate: string
  endDate: string
  isActive: boolean
  displayAsDiscount?: boolean
  createdAt?: string
  updatedAt?: string
}

interface DiscountsFormProps {
  initialValues: TourDiscount
  onSubmit: (values: TourDiscount) => void
  onCancel: () => void
  intl: IntlShape
  tourId: string
  isSubmitting?: boolean
  /** TTC / person from general tour pricing; used only for fixed-amount ↔ effective price sync in BO */
  referencePricePerPersonTTC?: number | null
}

const validationSchema = Yup.object().shape({
  minParticipants: Yup.number()
    .required('Required')
    .min(1, 'Minimum participants must be at least 1')
    .integer('Must be a whole number'),
  discountType: Yup.string()
    .required('Required')
    .oneOf(['fixed_amount', 'percentage'], 'Invalid discount type'),
  discountValue: Yup.number()
    .required('Required')
    .when('discountType', {
      is: 'percentage',
      then: (schema) =>
        schema
          .min(0.01, 'Discount value must be greater than 0')
          .max(100, 'Percentage cannot exceed 100%'),
      otherwise: (schema) => schema.min(0, 'Discount cannot be negative'),
    }),
  startDate: Yup.date().required('Start date is required'),
  endDate: Yup.date()
    .required('End date is required')
    .min(Yup.ref('startDate'), 'End date must be after start date'),
  isActive: Yup.boolean().required('Required'),
})

const DiscountsForm = forwardRef<{ handleSubmit: () => void }, DiscountsFormProps>(
  ({ initialValues, onSubmit, onCancel, intl, tourId, isSubmitting, referencePricePerPersonTTC }, ref) => {
    let formikRef: any = null
    const [effectivePriceFocused, setEffectivePriceFocused] = useState(false)
    const [effectivePriceDraft, setEffectivePriceDraft] = useState('')
    const [tierTotalFocused, setTierTotalFocused] = useState(false)
    const [tierTotalDraft, setTierTotalDraft] = useState('')

    useImperativeHandle(ref, () => ({
      handleSubmit: () => {
        if (formikRef) {
          formikRef.handleSubmit()
        }
      },
    }))

    return (
      <Formik
        initialValues={{
          ...initialValues,
          minParticipants: initialValues.minParticipants || 5,
          discountType: initialValues.discountType || 'fixed_amount',
          discountValue: initialValues.discountValue || 0,
          startDate: initialValues.startDate ? new Date(initialValues.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          endDate: initialValues.endDate ? new Date(initialValues.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          isActive: typeof initialValues.isActive === 'boolean' ? initialValues.isActive : true,
          displayAsDiscount: initialValues.displayAsDiscount === true,
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={(formik) => {
          formikRef = formik
        }}
      >
        {({ values, errors, touched, setFieldValue }) => {
          // Type casting for proper TypeScript typing
          const typedErrors = errors as FormikErrors<TourDiscount>;
          const typedTouched = touched as { [K in keyof TourDiscount]?: boolean };

          const base = referencePricePerPersonTTC != null && referencePricePerPersonTTC > 0
            ? referencePricePerPersonTTC
            : null
          const n = Number(values.minParticipants) || 0
          const discountNum = Number(values.discountValue)

          const showEffectivePrice =
            values.discountType === 'fixed_amount' && base != null && n >= 1

          const effectiveFromDiscount =
            showEffectivePrice && !Number.isNaN(discountNum) && base != null
              ? tierPricePerPersonFromDiscount(base, n, discountNum)
              : null

          const totalFromDiscount =
            showEffectivePrice && !Number.isNaN(discountNum) && base != null
              ? tierTotalTTCFromDiscount(base, n, discountNum)
              : null

          return (
            <div className='form'>
              <div className='row mb-6'>
                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.DISCOUNTS.MIN_PARTICIPANTS' })}
                  </label>
                  <Field name='minParticipants'>
                    {({ field }: FieldProps<number>) => (
                      <input
                        {...field}
                        type='number'
                        min='1'
                        step='1'
                        className={`form-control form-control-solid ${
                          typedErrors.minParticipants && typedTouched.minParticipants ? 'is-invalid' : ''
                        }`}
                        value={field.value === undefined || field.value === null ? '' : field.value}
                        onChange={(e) => {
                          field.onChange(e)
                          setEffectivePriceFocused(false)
                          setTierTotalFocused(false)
                        }}
                      />
                    )}
                  </Field>
                  {typedErrors.minParticipants && typedTouched.minParticipants && (
                    <div className='invalid-feedback'>{typedErrors.minParticipants as string}</div>
                  )}
                </div>

                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.DISCOUNTS.TYPE' })}
                  </label>
                  <Field name='discountType'>
                    {({ field }: FieldProps<string>) => (
                      <select
                        {...field}
                        className={`form-select form-select-solid ${
                          typedErrors.discountType && typedTouched.discountType ? 'is-invalid' : ''
                        }`}
                        onChange={(e) => {
                          field.onChange(e)
                          setEffectivePriceFocused(false)
                          setEffectivePriceDraft('')
                          setTierTotalFocused(false)
                          setTierTotalDraft('')
                        }}
                      >
                        <option value='fixed_amount'>
                          {intl.formatMessage({ id: 'TOUR.DISCOUNTS.FIXED_AMOUNT' })}
                        </option>
                        <option value='percentage'>
                          {intl.formatMessage({ id: 'TOUR.DISCOUNTS.PERCENTAGE' })}
                        </option>
                      </select>
                    )}
                  </Field>
                  {typedErrors.discountType && typedTouched.discountType && (
                    <div className='invalid-feedback'>{typedErrors.discountType as string}</div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.DISCOUNTS.VALUE' })}
                  </label>
                  <div className='input-group'>
                    <Field name='discountValue'>
                      {({ field }: FieldProps<number>) => (
                        <input
                          {...field}
                          type='number'
                          min={values.discountType === 'percentage' ? '0.01' : '0'}
                          step='0.01'
                          className={`form-control form-control-solid ${
                            typedErrors.discountValue && typedTouched.discountValue ? 'is-invalid' : ''
                          }`}
                          value={
                            field.value === undefined || field.value === null
                              ? ''
                              : field.value
                          }
                          onChange={(e) => {
                            field.onChange(e)
                            setEffectivePriceFocused(false)
                            setTierTotalFocused(false)
                          }}
                        />
                      )}
                    </Field>
                    <span className='input-group-text'>
                      {values.discountType === 'percentage' ? '%' : '€'}
                    </span>
                  </div>
                  {typedErrors.discountValue && typedTouched.discountValue && (
                    <div className='invalid-feedback'>{typedErrors.discountValue as string}</div>
                  )}
                  <div className='form-text'>
                    {values.discountType === 'percentage'
                      ? intl.formatMessage({ id: 'TOUR.DISCOUNTS.PERCENTAGE_HELP' })
                      : intl.formatMessage({ id: 'TOUR.DISCOUNTS.FIXED_AMOUNT_HELP' })}
                  </div>
                  {showEffectivePrice && (
                    <>
                      <label className='form-label mt-4'>
                        {intl.formatMessage({ id: 'TOUR.DISCOUNTS.EFFECTIVE_PRICE_PER_PERSON' })}
                      </label>
                      <div className='input-group'>
                        <input
                          type='number'
                          min='0'
                          step='0.01'
                          className='form-control form-control-solid'
                          value={
                            effectivePriceFocused
                              ? effectivePriceDraft
                              : effectiveFromDiscount != null && !Number.isNaN(effectiveFromDiscount)
                                ? String(effectiveFromDiscount)
                                : ''
                          }
                          onFocus={() => {
                            setEffectivePriceFocused(true)
                            setTierTotalFocused(false)
                            if (effectiveFromDiscount != null && !Number.isNaN(effectiveFromDiscount)) {
                              setEffectivePriceDraft(String(effectiveFromDiscount))
                            } else {
                              setEffectivePriceDraft('')
                            }
                          }}
                          onChange={(e) => {
                            const raw = e.target.value
                            setEffectivePriceDraft(raw)
                            if (base == null || n < 1) return
                            const eff = parseFloat(raw)
                            if (Number.isNaN(eff)) return
                            const newDiscount = roundMoney(base * n - eff * n)
                            void setFieldValue('discountValue', Math.max(0, newDiscount), true)
                          }}
                          onBlur={() => {
                            setEffectivePriceFocused(false)
                            if (
                              effectiveFromDiscount != null &&
                              !Number.isNaN(effectiveFromDiscount)
                            ) {
                              setEffectivePriceDraft(String(effectiveFromDiscount))
                            }
                          }}
                        />
                        <span className='input-group-text'>€</span>
                      </div>
                      <div className='form-text'>
                        {intl.formatMessage(
                          { id: 'TOUR.DISCOUNTS.EFFECTIVE_PRICE_PER_PERSON_HELP' },
                          {
                            count: n,
                            base: base?.toFixed(2),
                          }
                        )}
                      </div>

                      <label className='form-label mt-4'>
                        {intl.formatMessage({ id: 'TOUR.DISCOUNTS.TIER_TOTAL_TTC' })}
                      </label>
                      <div className='input-group'>
                        <input
                          type='number'
                          min='0'
                          step='0.01'
                          className='form-control form-control-solid'
                          value={
                            tierTotalFocused
                              ? tierTotalDraft
                              : totalFromDiscount != null && !Number.isNaN(totalFromDiscount)
                                ? String(totalFromDiscount)
                                : ''
                          }
                          onFocus={() => {
                            setTierTotalFocused(true)
                            setEffectivePriceFocused(false)
                            if (totalFromDiscount != null && !Number.isNaN(totalFromDiscount)) {
                              setTierTotalDraft(String(totalFromDiscount))
                            } else {
                              setTierTotalDraft('')
                            }
                          }}
                          onChange={(e) => {
                            const raw = e.target.value
                            setTierTotalDraft(raw)
                            if (base == null || n < 1) return
                            const totalInput = parseFloat(raw)
                            if (Number.isNaN(totalInput)) return
                            void setFieldValue(
                              'discountValue',
                              discountFromTierTotal(base, n, totalInput),
                              true
                            )
                          }}
                          onBlur={() => {
                            setTierTotalFocused(false)
                            if (totalFromDiscount != null && !Number.isNaN(totalFromDiscount)) {
                              setTierTotalDraft(String(totalFromDiscount))
                            }
                          }}
                        />
                        <span className='input-group-text'>€</span>
                      </div>
                      <div className='form-text'>
                        {intl.formatMessage({ id: 'TOUR.DISCOUNTS.TIER_TOTAL_TTC_HELP' })}
                      </div>
                    </>
                  )}
                  {values.discountType === 'fixed_amount' && base == null && (
                    <div className='form-text text-warning mt-2'>
                      {intl.formatMessage({ id: 'TOUR.DISCOUNTS.NO_BASE_PRICE_FOR_SYNC' })}
                    </div>
                  )}
                </div>

                <div className='col-md-12'>
                  <div className='form-check form-switch'>
                    <Field name='displayAsDiscount'>
                      {({ field }: FieldProps) => (
                        <input
                          type='checkbox'
                          className='form-check-input'
                          role='switch'
                          id='displayAsDiscount'
                          checked={field.value === true}
                          onChange={(e) => setFieldValue('displayAsDiscount', e.target.checked)}
                        />
                      )}
                    </Field>
                    <label className='form-check-label ms-2' htmlFor='displayAsDiscount'>
                      {intl.formatMessage({ id: 'TOUR.DISCOUNTS.DISPLAY_AS_DISCOUNT' })}
                    </label>
                  </div>
                  <p className='text-muted fs-7 mt-2 mb-0'>
                    {intl.formatMessage({ id: 'TOUR.DISCOUNTS.DISPLAY_AS_DISCOUNT_HELP' })}
                  </p>
                </div>

                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.DISCOUNTS.ACTIVE_STATUS' })}
                  </label>
                  <div className='form-check form-switch mt-2'>
                    <Field
                      className='form-check-input'
                      type='checkbox'
                      role='switch'
                      id='isActive'
                      name='isActive'
                    />
                    <label className='form-check-label' htmlFor='isActive'>
                      {values.isActive
                        ? intl.formatMessage({ id: 'TOUR.DISCOUNTS.ACTIVE' })
                        : intl.formatMessage({ id: 'TOUR.DISCOUNTS.INACTIVE' })}
                    </label>
                  </div>
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.DISCOUNTS.START_DATE' })}
                  </label>
                  <Field
                    type='date'
                    name='startDate'
                    className={`form-control form-control-solid ${
                      typedErrors.startDate && typedTouched.startDate ? 'is-invalid' : ''
                    }`}
                  />
                  {typedErrors.startDate && typedTouched.startDate && (
                    <div className='invalid-feedback'>{typedErrors.startDate as string}</div>
                  )}
                </div>

                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.DISCOUNTS.END_DATE' })}
                  </label>
                  <Field
                    type='date'
                    name='endDate'
                    className={`form-control form-control-solid ${
                      typedErrors.endDate && typedTouched.endDate ? 'is-invalid' : ''
                    }`}
                  />
                  {typedErrors.endDate && typedTouched.endDate && (
                    <div className='invalid-feedback'>{typedErrors.endDate as string}</div>
                  )}
                </div>
              </div>

              <div className='d-flex justify-content-end mt-6'>
                <button
                  type='button'
                  className='btn btn-light me-3'
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  {intl.formatMessage({ id: 'TOUR.ACTIONS.CANCEL' })}
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={() => formikRef.handleSubmit()}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                  ) : null}
                  {intl.formatMessage({ id: 'TOUR.ACTIONS.SAVE' })}
                </button>
              </div>
            </div>
          )
        }}
      </Formik>
    )
  }
)

export default DiscountsForm 